import React, {FC, useEffect} from 'react';
import {Alert} from '@deere/fuel-react';
import {Button, Card, Container, Grid, Typography} from '@mui/material';

import {useOktaAuth} from '@okta/okta-react';
import Snackbar from '@mui/material/Snackbar';

const TIMEOUT = 5000;
const NO_CONTENT_STATUS = 204;

export const JDUBilling: FC<unknown> = () => {
    const {authState} = useOktaAuth();

    const [state, setState] = React.useState({
        currentbillingrundate: '',
        currentbillingperiodbegin: '',
        currentbillingperiodend: '',
        nextbillingrundate: '',
        nextbillingperiodbegin: '',
        nextbillingperiodend: '',
        message: '',
        showMessage: false
    });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/billingdates`, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            }
        })
            .then((response) => {
                return response.json();
            })
            .then((billingdates) => {
                setState((prevState) => ({
                    ...prevState,
                    currentbillingrundate: billingdates.currentbillingrundate,
                    currentbillingperiodbegin: billingdates.currentbillingperiodbegin,
                    currentbillingperiodend: billingdates.currentbillingperiodend,
                    nextbillingrundate: billingdates.nextbillingrundate,
                    nextbillingperiodbegin: billingdates.nextbillingperiodbegin,
                    nextbillingperiodend: billingdates.nextbillingperiodend
                }));
                return null;
            }).catch((error) => {
                console.log(error);
            });
    }, [authState]);

    const handleDownloadFileButton = (): void => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/downloadbillingreport`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/octet-stream'
            }
        })
            .then((response) => {
                if (response.status === NO_CONTENT_STATUS) {
                    setState((prevState) => ({
                        ...prevState,
                        message: 'No Records Available for Current Billing Period. Please try again later.',
                        showMessage: true
                    }));
                    setTimeout(() => {
                        setState((prevState) => ({
                            ...prevState,
                            message: '',
                            showMessage: false
                        }));
                    }, TIMEOUT);
                    return null;
                }
                return response.blob();
            })
            .then((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');

                    link.href = url;
                    link.download = 'JDU Unbilled Validation Report.xlsx';
                    link.click();
                    URL.revokeObjectURL(url);
                }
                return null;
            })
            .catch((error) => {
                console.error('Error fetching the data:', error);
            });
    };

    const handleCloseCycleButton = (): void => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/closebillingcycle`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/octet-stream'
            }
        })
            .then((response) => {
                return response.text();
            })
            .then((data) => {
                setState((prevState) => ({
                    ...prevState,
                    message: data,
                    showMessage: true
                }));

                setTimeout(() => {
                    setState((prevState) => ({
                        ...prevState,
                        message: '',
                        showMessage: false
                    }));
                }, TIMEOUT);
                return null;
            })
            .catch((error) => {
                console.error('Error in closing the billing cycle: ', error);
            });
    };

    return (
        <>
            <Container
                sx={{
                    paddingTop: '1rem',
                    textAlign: 'center'
                }}
            >
                <Card>
                    <Grid
                        alignItems='center'
                        container
                        justifyContent='center'
                        spacing={2}
                    >
                        <Grid
                            item
                            marginTop={2}
                            xs={12}
                        >
                            <Typography
                                sx={
                                    {
                                        textAlign: 'center'
                                    }
                                }
                                variant='h2'
                            >
                                {'JDU Billing'}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            marginTop={4}
                            xs={6}
                        >
                            <Typography
                                sx={
                                    {
                                        textAlign: 'right'
                                    }
                                }
                                variant='h6'
                            >
                                {'Current Billing Run Date :'} {state.currentbillingrundate}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            marginTop={4}
                            xs={6}
                        >
                            <Typography
                                sx={
                                    {
                                        paddingLeft: '2rem',
                                        textAlign: 'left'
                                    }
                                }
                                variant='h6'
                            >
                                {'Billing period: '} {state.currentbillingperiodbegin} {' to '} {state.currentbillingperiodend}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            marginTop={4}
                            xs={6}
                        >
                            <Typography
                                sx={
                                    {
                                        textAlign: 'right'
                                    }
                                }
                                variant='h6'
                            >
                                {'Next Billing Run Date :'} {state.nextbillingrundate}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            marginTop={4}
                            xs={6}
                        >
                            <Typography
                                sx={
                                    {
                                        paddingLeft: '2rem',
                                        textAlign: 'left'
                                    }
                                }
                                variant='h6'
                            >
                                {'Billing period: '} {state.nextbillingperiodbegin} {' to '} {state.nextbillingperiodend}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                        >
                            <Button
                                onClick={handleDownloadFileButton}
                                size='medium'
                                style={{
                                    marginTop: '4rem'
                                }}
                                variant='primary'
                            >
                                {'Download File'}
                            </Button>
                            <Button
                                onClick={handleCloseCycleButton}
                                size='medium'
                                style={{
                                    marginLeft: '3rem',
                                    marginTop: '4rem'
                                }}
                                variant='primary'
                            >
                                {'Close Cycle'}
                            </Button>
                            <br/><br/>
                        </Grid>
                        <Grid
                            item
                            marginBottom={4}
                            marginTop={2}
                            xs={12}
                        >
                            <Alert
                                variant='warning'
                            >
                                {'Please note: Hitting "Close Cycle" button signifies that the billing data for current month was reviewed and approved.'}
                            </Alert>
                        </Grid>
                    </Grid>
                </Card>
            </Container>
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                message={state.message}
                open={state.showMessage}
            />
        </>
    );
};
